/**
 * Error Reporter
 *
 * Captures uncaught JS errors and unhandled promise rejections and reports
 * them both to PostHog and to the server-side API endpoint so crashes are
 * detectable without a human watching the screen.
 *
 * Runs unconditionally in production builds — this is a dedicated
 * tablet-kiosk display and we always want error visibility. Skipped in dev.
 *
 * Throttle: at most one report per 10 seconds per distinct error message hash
 * to prevent storms after a catastrophic failure.
 *
 * The throttle Map is bounded to MAX_TRACKED_KEYS entries (LRU eviction) and
 * entries older than ENTRY_TTL_MS are pruned on each write to prevent unbounded
 * memory growth over long uptimes.
 */

const THROTTLE_WINDOW_MS = 10_000;
const ENTRY_TTL_MS = 60 * 60 * 1000; // 1 hour — evict stale entries
const MAX_TRACKED_KEYS = 100; // LRU cap
const API_ENDPOINT = '/api/v1/kiosk-error';

/** Simple djb2-style string hash, returns hex string */
function hashString(str: string): string {
  let h = 5381;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) + h) ^ str.charCodeAt(i);
    h = h >>> 0; // keep unsigned 32-bit
  }
  return h.toString(16);
}

/**
 * Bounded throttle Map: capped at MAX_TRACKED_KEYS via LRU eviction,
 * with time-based expiry at ENTRY_TTL_MS to prevent multi-hour drift.
 */
const lastReportedAt = new Map<string, number>();

function evictStaleEntries(now: number): void {
  for (const [key, ts] of lastReportedAt) {
    if (now - ts > ENTRY_TTL_MS) {
      lastReportedAt.delete(key);
    }
  }
}

function shouldThrottle(message: string): boolean {
  const key = hashString(message);
  const now = Date.now();

  // Prune expired entries on every write
  evictStaleEntries(now);

  const last = lastReportedAt.get(key);
  if (last !== undefined && now - last < THROTTLE_WINDOW_MS) {
    return true;
  }

  // LRU eviction: if at cap, remove oldest entry before inserting
  if (!lastReportedAt.has(key) && lastReportedAt.size >= MAX_TRACKED_KEYS) {
    const oldestKey = lastReportedAt.keys().next().value;
    if (oldestKey !== undefined) {
      lastReportedAt.delete(oldestKey);
    }
  }

  lastReportedAt.set(key, now);
  return false;
}

interface ErrorPayload {
  message: string;
  stack?: string;
  source?: string;
  lineno?: number;
  colno?: number;
  url: string;
  userAgent: string;
  // Always true: kiosk gating removed in PR #19; field required by server Zod schema (kiosk-error.ts:26)
  kioskMode: boolean;
}

function buildPayload(overrides: Partial<ErrorPayload> & { message: string }): ErrorPayload {
  return {
    url: window.location.href,
    userAgent: navigator.userAgent,
    kioskMode: true,
    ...overrides,
  };
}

function reportToPosthog(payload: ErrorPayload): void {
  try {
    const ph = (window as unknown as Record<string, unknown>)['posthog'];
    if (ph && typeof (ph as { capture?: unknown }).capture === 'function') {
      (ph as { capture: (event: string, props: unknown) => void }).capture(
        'kiosk_js_error',
        payload
      );
    }
  } catch {
    // posthog unavailable — swallow
  }
}

function reportToApi(payload: ErrorPayload): void {
  try {
    fetch(API_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      // Best-effort: no error handling, no retry
    }).catch(() => {});
  } catch {
    // fetch itself threw — swallow
  }
}

function report(payload: ErrorPayload): void {
  if (shouldThrottle(payload.message)) return;
  reportToPosthog(payload);
  reportToApi(payload);
}

export function initKioskErrorReporter(): void {
  // Skip in dev so console noise doesn't fire API calls during development
  if (import.meta.env.DEV) return;

  window.addEventListener('error', (event: ErrorEvent) => {
    const message = event.message || 'Unknown error';
    report(
      buildPayload({
        message,
        stack: event.error?.stack,
        source: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      })
    );
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    const message =
      reason instanceof Error
        ? reason.message
        : typeof reason === 'string'
          ? reason
          : 'Unhandled promise rejection';
    report(
      buildPayload({
        message,
        stack: reason instanceof Error ? reason.stack : undefined,
      })
    );
  });
}
